import React, { useRef, useEffect } from 'react';
import './Conversation.css';
import type { ChatMessageDto } from '../types/ChatMessageDto';
import MessageFeedback from './MessageFeedback';
import Spinner from './Spinner';
import AdSense from './AdSense';
import { isAdsEnabled } from '../utils/featureFlags';

interface ConversationProps {
  messages: ChatMessageDto[];
  loading?: boolean;
  error?: string;
  emptyMessage?: string;
  autoScroll?: boolean;
  showFeedback?: boolean;
}

const Conversation: React.FC<ConversationProps> = ({
  messages,
  loading = false,
  error,
  emptyMessage = 'No messages yet. Start the conversation by typing a question below.',
  autoScroll = true,
  showFeedback = true,
}) => {
  const threadRef = useRef<HTMLDivElement | null>(null);
  const lastUserMsgRef = useRef<HTMLDivElement | null>(null);

  // Find the index of the last user message for scroll targeting
  const lastUserIndex = messages.reduce((acc, m, idx) => (m.role === 'user' ? idx : acc), -1);

  // Scroll behavior on new messages:
  // Always scroll the latest user bubble to the top of the thread
  useEffect(() => {
    if (!autoScroll || !messages.length) {
      return;
    }
    if (lastUserMsgRef.current) {
      lastUserMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages, autoScroll]);

  return (
    <>
      {messages.length === 0 && !loading && !error && (
        <div className="alert alert-info" role="alert">
          {emptyMessage}
        </div>
      )}

      {!error && (
        <div className="conversation-thread" ref={threadRef} aria-live="polite">
          {messages.map((m, idx) => (
            <div>
              <div
                key={idx}
                ref={idx === lastUserIndex ? lastUserMsgRef : undefined}
                className={`conversation-msg ${m.role === 'user' ? 'msg-user' : 'msg-assistant'}`}
                data-message-id={m.messageId}
              >
                {m.role === 'user' ? (
                  <>
                    <div className="bubble">{m.content}</div>
                  </>
                ) : (
                  <>
                    <div className="bubble" dangerouslySetInnerHTML={{ __html: m.content }} />
                    {showFeedback && <MessageFeedback message={m} />}
                  </>
                )}
              </div>
              {m.role === 'user' && isAdsEnabled() && (
                <div className="d-block d-lg-none my-2 chat-ad-inline">
                  <AdSense
                    adClient={process.env.REACT_APP_ADSENSE_CLIENT_ID || ''}
                    adSlot={process.env.REACT_APP_ADSENSE_SLOT_HORIZONTAL || ''}
                    adFormat="horizontal"
                    fullWidthResponsive={true}
                  />
                </div>
              )}
            </div>
          ))}
          <Spinner visible={loading} ariaLabel="Thinking…" />
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}
    </>
  );
};

export default Conversation;
